import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { parse } from 'querystringify'

// Actions
import { actions as collectionsActions } from 'modules/collections/actions'
import { actions as browseActions } from 'modules/browse/actions'
import { actions as recommendationsActions } from 'modules/recommendations/actions'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useCollections, useTracks } from 'modules/entities/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useCreateSearchQuery } from 'modules/browse/hooks'

// Types
import { Collection, State, Loop } from 'types'

// Selectors
import { recommendationsSelector } from 'modules/recommendations/selectors'

// Utils
import { TRACKS_PER_PAGE } from 'components/browse/tracklist'

export const useCollection = (id?: number) => {
  const dispatch = useDispatch()
  const state = useSelector<State, State>(s => s)
  const profile = useProfile()
  const collection = state.entities.collections[String(id)]
  let fetchCollectionRequest = useRequest('FETCH_COLLECTION', String(id))
  const [hasFetchedCollection, setFetchedCollection] = useState(false)

  useEffect(() => {
    if (id && !collection && !fetchCollectionRequest.isLoading && !fetchCollectionRequest.hasError) {
      dispatch(collectionsActions.fetchCollection(+id))
    }
  }, [id, collection, profile, fetchCollectionRequest, dispatch])

  fetchCollectionRequest = useRequest('FETCH_COLLECTION', String(id))

  useEffect(() => {
    // NOTE(Kitti): This effect only used to load private collections for sxtaff
    if (!hasFetchedCollection && profile.is_staff && id && !collection && profile.id !== 0 && !fetchCollectionRequest.isLoading) {
      dispatch(collectionsActions.fetchCollection(+id))
      setFetchedCollection(true)
    }
  }, [hasFetchedCollection, id, collection, profile, dispatch, fetchCollectionRequest])

  return { collection, request: fetchCollectionRequest }
}

export const useCollectionSearchQuery = (collection: Collection) => {
  const dispatch = useDispatch()
  const allTracks = useTracks()
  const collectionPages = (collection?.tracks || []).reduce((agg, colTrack, index) => {
    const localIndex = index % TRACKS_PER_PAGE
    const page = (index - localIndex) / TRACKS_PER_PAGE
    if (!agg[page]) {
      return [...agg, [colTrack]]
    }
    return [...agg.slice(0, page), [...agg[page], colTrack]] // ,
  }, [])
  const { agg: pagesWithFetchedSongs = [] } = collectionPages.reduce(
    ({ agg, lastPageFail }, page) => {
      if (lastPageFail) {
        return false
      }
      const pageHasAllTracks = !page.some(({ id: trackId }) => !allTracks[String(trackId)])
      if (pageHasAllTracks) {
        return { agg: [...agg, page], lastPageFail: false }
      }
      return { agg, lastPageFail: true }
    },
    { agg: [], lastPageFail: false },
  )

  const collectionTrackIds = pagesWithFetchedSongs?.reduce((agg, page) => [...agg, ...page], [])
  const collectionFirstPageTrackIds = collectionPages ? collectionPages[0]?.map(({ id: trackId }) => trackId) : []
  const nextPageToFetchIndex = pagesWithFetchedSongs?.length
  const nextPageToFetchTrackIds = pagesWithFetchedSongs
    ? collectionPages[nextPageToFetchIndex]?.map(({ id: trackId }) => trackId).filter(trackId => !allTracks[String(trackId)])
    : []
  const firstPageIds = [...new Set(collectionFirstPageTrackIds)]
  const firstPageSearchQuery = `ids=${firstPageIds.join('__')}`
  const nextPageSearchQuery = `ids=${[...new Set(nextPageToFetchTrackIds)].join('__')}`

  let fetchFirstPageRequest = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', firstPageSearchQuery)
  if (collection && !pagesWithFetchedSongs.length && !fetchFirstPageRequest.isLoading && !fetchFirstPageRequest.isDone) {
    dispatch(browseActions.fetchTracksBySearchQuery(firstPageSearchQuery))
  }
  fetchFirstPageRequest = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', firstPageSearchQuery) // Need to update it here in case fetchQuery fired
  const fetchNextPageRequest = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', nextPageSearchQuery)
  const trackListRequest =
    pagesWithFetchedSongs?.length && (fetchNextPageRequest.isLoading || fetchNextPageRequest.isDone)
      ? fetchNextPageRequest
      : fetchFirstPageRequest

  // Fetch searchconfig for current query but only on first load
  const tracksCount = collection?.tracks?.length
  const collectionId = collection?.id || 0
  const searchQuery = useCreateSearchQuery({ collection: String(collectionId) }, true)
  const fetchSearchConfigRequest = useRequest('FETCH_SEARCH_CONFIG', searchQuery)
  useEffect(() => {
    if (!fetchSearchConfigRequest.isLoading && !fetchSearchConfigRequest.hasError) {
      dispatch(browseActions.fetchSearchConfig(searchQuery))
    }
  }, [tracksCount, collectionId]) // eslint-disable-line

  // Fetch recommendations
  const state = useSelector<State, State>((s: State) => s)
  const recommendations = recommendationsSelector(state, 'recommendationsCollection', String(collectionId))
  const { recommendedArtists, recommendedTrackIds, recommendedCollections } = recommendations
  const recommendationsRequest = useRequest('FETCH_COLLECTION_RECOMMENDATIONS', collectionId)
  useEffect(() => {
    if (!recommendationsRequest.isLoading && !recommendationsRequest.hasError) {
      dispatch(recommendationsActions.fetchCollectionRecommendations(collectionId))
    }
  }, [collectionId, dispatch]) // eslint-disable-line

  return {
    collectionTrackIds,
    loadMore: () => dispatch(browseActions.fetchTracksBySearchQuery(nextPageSearchQuery)),
    trackListRequest,
    fetchFirstPageRequest,
    recommendationsRequest,
    recommendedArtists,
    recommendedTrackIds,
    recommendedCollections,
  }
}

export const useFavoritesCollectionId = () => {
  const profile = useProfile()
  return profile?.favorites_collection_id
}

export const useIsInFavoritesCollection = (trackId: number, loop?: Loop | null) => {
  const collections = useCollections()
  const favoritesCollectionId = useFavoritesCollectionId()
  const collection = collections[String(favoritesCollectionId)]
  const isInFavorites = loop
    ? collection?.tracks.some(
        ({ id, loop: innerLoop }) =>
          innerLoop && id === trackId && innerLoop.beat_count === loop.beat_count && innerLoop.beat_start === loop.beat_start,
      )
    : collection?.tracks.some(({ id, loop: innerLoop }) => +id === +trackId && innerLoop === null)

  return isInFavorites
}

export const useOwnCollections = (): Array<Collection> => {
  const collections = useCollections()
  const profile = useProfile()

  if (!profile) {
    return []
  }

  return Object.keys(collections)
    .map(id => collections[id])
    .filter(x => x.owner === profile.user && x.id !== profile.favorites_collection_id)
}

export const useIsInAnyCollection = (trackId: number, loop?: Loop) => {
  const ownCollections = useOwnCollections()
  if (!ownCollections.length) {
    return false
  }
  const isInCollection = loop
    ? ownCollections.some(collection =>
        collection.tracks.some(
          ({ id, loop: innerLoop }) =>
            innerLoop && id === trackId && innerLoop.beat_count === loop.beat_count && innerLoop.beat_start === loop.beat_start,
        ),
      )
    : ownCollections.some(collection => collection.tracks.some(({ id, loop: innerLoop }) => id === trackId && innerLoop === null))

  return isInCollection
}

export const useTrackIdAndLoopFromSearch = (): { trackId: number; loop?: Loop } => {
  const { search } = useLocation()
  const { trackId, loop: loopString } = parse(search) as { trackId: string; loop: string }
  const [beatStart, beatCount] = loopString ? loopString.split(':') : []
  const loop = loopString ? { beat_start: +beatStart, beat_count: +beatCount } : undefined
  return { trackId: +trackId, loop }
}
